/**
 * Walkthrough Context
 *
 * Lightweight context that allows pages to register their tour config
 * and the Header to read it for the Help button.
 */

import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import type { TourConfig } from './types';

interface WalkthroughContextType {
  /** The currently active page's tour config */
  activeTour: TourConfig | null;
  /** Replay function for the current page's tour */
  replayTour: (() => void) | null;
  /** Whether walkthroughs are enabled for this user */
  walkthroughEnabled: boolean;
  /** Register a page tour (called by usePageTour) */
  registerTour: (config: TourConfig, replay: () => void) => void;
  /** Unregister when page unmounts */
  unregisterTour: () => void;
  /** Set whether walkthroughs are enabled */
  setWalkthroughEnabled: (enabled: boolean) => void;
}

const WalkthroughCtx = createContext<WalkthroughContextType | undefined>(undefined);

export function WalkthroughProvider({ children }: { children: ReactNode }) {
  const [activeTour, setActiveTour] = useState<TourConfig | null>(null);
  const [replayTour, setReplayTour] = useState<(() => void) | null>(null);
  const [walkthroughEnabled, setWalkthroughEnabled] = useState(false);

  const registerTour = useCallback((config: TourConfig, replay: () => void) => {
    setActiveTour(config);
    // Wrap in another function to avoid React treating the function as a state updater
    setReplayTour(() => replay);
  }, []);

  const unregisterTour = useCallback(() => {
    setActiveTour(null);
    setReplayTour(null);
  }, []);

  return (
    <WalkthroughCtx.Provider
      value={{
        activeTour,
        replayTour,
        walkthroughEnabled,
        registerTour,
        unregisterTour,
        setWalkthroughEnabled,
      }}
    >
      {children}
    </WalkthroughCtx.Provider>
  );
}

const NO_OP = () => {};

/** Safe fallback when used outside the provider (e.g., login page) */
const DEFAULT_CTX: WalkthroughContextType = {
  activeTour: null,
  replayTour: null,
  walkthroughEnabled: false,
  registerTour: NO_OP,
  unregisterTour: NO_OP,
  setWalkthroughEnabled: NO_OP,
};

export function useWalkthroughContext() {
  const ctx = useContext(WalkthroughCtx);
  return ctx ?? DEFAULT_CTX;
}
